// Tiny cookie helper for the theme preference.
//
// Stores the user's selected theme mode in a long-lived first-party cookie so
// the next page load can apply the correct palette before React hydrates.
// The server-side preference (when authenticated) is the eventual source of
// truth and overrides this cookie when the API call resolves.

export type ThemePreference = 'light' | 'dark' | 'auto'

const COOKIE_NAME = 'qa_theme'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365
const VALID_VALUES: ReadonlyArray<ThemePreference> = ['light', 'dark', 'auto']

function isThemePreference(value: string): value is ThemePreference {
  return (VALID_VALUES as ReadonlyArray<string>).includes(value)
}

// Reads qa_theme from document.cookie. Returns the parsed value when it is
// one of the three valid strings. Returns null when the cookie is missing,
// malformed, or holds an unrecognized value. Defensive against missing
// document (SSR / test sandboxes) and against multiple cookies sharing the
// same prefix.
export function readThemeCookie(): ThemePreference | null {
  if (typeof document === 'undefined') return null

  const raw = document.cookie
  if (!raw) return null

  const parts = raw.split(';')
  for (const part of parts) {
    const trimmed = part.trim()
    if (!trimmed) continue
    const eq = trimmed.indexOf('=')
    if (eq === -1) continue

    const name = trimmed.slice(0, eq).trim()
    if (name !== COOKIE_NAME) continue

    const rawValue = trimmed.slice(eq + 1).trim()
    let value: string
    try {
      value = decodeURIComponent(rawValue)
    } catch {
      // Malformed percent-encoding — treat as missing.
      return null
    }

    return isThemePreference(value) ? value : null
  }

  return null
}

// Writes qa_theme with a one-year max-age. Adds Secure when running on https
// so the cookie isn't downgraded over a mixed-content connection. Skips
// Secure on http (localhost dev) where the flag would prevent the cookie
// from being stored at all.
export function writeThemeCookie(theme: ThemePreference): void {
  if (typeof document === 'undefined') return

  const isHttps =
    typeof window !== 'undefined' && window.location && window.location.protocol === 'https:'

  const segments = [
    `${COOKIE_NAME}=${encodeURIComponent(theme)}`,
    'Path=/',
    `Max-Age=${ONE_YEAR_SECONDS}`,
    'SameSite=Lax',
  ]
  if (isHttps) segments.push('Secure')

  document.cookie = segments.join('; ')
}

// Clears the cookie (handy for tests and account switches). Mirrors the
// attributes of writeThemeCookie so the browser can match and remove the
// existing entry.
export function clearThemeCookie(): void {
  if (typeof document === 'undefined') return

  const isHttps =
    typeof window !== 'undefined' && window.location && window.location.protocol === 'https:'

  const segments = [`${COOKIE_NAME}=`, 'Path=/', 'Max-Age=0', 'SameSite=Lax']
  if (isHttps) segments.push('Secure')

  document.cookie = segments.join('; ')
}
